import {
  CATALOG_FIELDS,
  HSN_FIELDS,
  VARIANT_FIELDS,
} from "@/modules/catalog/fields";
import { collectAll } from "@/components/table/export";
import { hsnListQuery } from "@/modules/catalog/hsnQuery";
import { projectHsnCode } from "@/modules/catalog/projections";
import { HsnMaster } from "@/modules/catalog/screens/HsnMaster";
import { MissingHsn } from "@/modules/catalog/screens/MissingHsn";
import { ProductList } from "@/modules/catalog/screens/ProductList";
import type { ModuleUi } from "@/modules/types";

/**
 * The Catalog module.
 *
 * Three sections, three row shapes — a product, an HSN code, a variant with
 * no code yet — so each gets its own field set rather than sharing one:
 * Missing HSN's column chooser opening onto `vendor` and `productType`
 * would describe a product, not the variant the row actually is.
 */
export const catalogModule: ModuleUi = {
  id: "catalog",
  sections: {
    products: {
      fields: CATALOG_FIELDS,
      /*
        The collection filter's options are the store's collections (#308,
        FR-CAT-9). The value is the title, because the title is what both
        adapters project `collections` as and what the column shows — an id
        here would be a filter that names something no row displays.
      */
      fieldOptions: async (data) => {
        const page = await data.catalog.listCollections({});
        return {
          collections: page.items.map((collection) => ({
            value: collection.title,
            label: collection.title,
          })),
        };
      },
      screen: ProductList,
      layouts: ["table", "grid", "tile"],
      commonFilters: ["status", "productType", "collections", "hasHsn"],
      count: async (data, query) =>
        (await data.catalog.listProducts({ ...query, limit: 1 })).total,
    },
    hsn: {
      fields: HSN_FIELDS,
      screen: HsnMaster,
      exportRows: (data, query) =>
        collectAll(
          (page) => data.catalog.listHsnCodes(page),
          hsnListQuery(query),
          projectHsnCode,
        ),
    },
    "missing-hsn": {
      fields: VARIANT_FIELDS,
      screen: MissingHsn,
      nothingYet: { title: "Every product has an HSN code" },
    },
  },
};
